import { useEffect, useMemo } from 'react';

import OptimizationPipelineSidebarTable from 'app/admin/optimizationPipelineV2/component/pipeline/sidebars/base/OptimizationPipelineSidebarTable';
import { SidebarExportButtons, SidebarHeaderSearch } from 'app/admin/optimizationPipelineV2/component/pipeline/sidebars/base/OptimizationPipelineSidebar';
import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';
import { exportSidebarTable, buildExportFilename } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/exportUtils';

const UNIT_MEASUREMENT_TABLE_CONFIG = {
    columns: [
        {
            key: 'attribute',
            label: 'Attribute',
            sortable: true,
            searchable: true,
            align: 'left',
            minWidth: 220,
            truncate: true,
            tooltip: true,
            cellClassName: 'whitespace-nowrap'
        },
        {
            key: 'value',
            label: 'Value',
            sortable: true,
            searchable: true,
            align: 'right',
            minWidth: 160,
            numeric: true,
            tooltip: true,
            cellClassName: 'whitespace-nowrap'
        },
        {
            key: 'uom',
            label: 'UOM',
            sortable: false,
            searchable: true,
            align: 'right',
            minWidth: 100,
            cellClassName: 'whitespace-nowrap'
        }
    ],
    search: {
        enabled: true,
        showInput: false,
        debounceMs: 250,
        placeholder: 'Search measurements...'
    },
    sort: {
        key: 'attribute',
        direction: 'asc'
    },
    states: {
        loadingTitle: 'Loading measurements...',
        emptyTitle: 'No unit measurement data available.',
        noResultsTitle: 'No matching measurements found.',
        errorTitle: 'Failed to load measurements',
        errorDescription: 'Please try again later.'
    }
};

function isPrimitive(value) {
    return value == null || typeof value !== 'object' || value instanceof Date;
}

function formatValue(value) {
    if (value == null || Number.isNaN(value)) return '-';
    if (typeof value === 'number') {
        const abs = Math.abs(value);

        if (abs >= 100) return value.toFixed(1);
        if (abs >= 1) return value.toFixed(3);

        return value.toFixed(5);
    }
    if (typeof value === 'boolean') return value ? 'true' : 'false';

    return String(value);
}

function parseComponents(components) {
    if (Array.isArray(components)) return components;
    if (components && typeof components === 'object') return Object.values(components);
    if (!components || typeof components !== 'string') return [];

    try {
        const parsed = JSON.parse(components);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
}

function buildUomMap(cleanMeasurementData, mergedMeasurementData) {
    const uomMap = new Map();
    const fields = [
        ...(Array.isArray(mergedMeasurementData?.fields) ? mergedMeasurementData.fields : []),
        ...(Array.isArray(cleanMeasurementData?.fields) ? cleanMeasurementData.fields : [])
    ];

    fields.forEach((field) => {
        if (!field?.name) return;

        const fieldName = String(field.name);
        const uom = field?.uom ?? null;

        uomMap.set(fieldName, uom);

        parseComponents(field.components).forEach((component) => {
            if (!component?.name) return;
            uomMap.set(fieldName + '.' + String(component.name), uom);
        });
    });

    return uomMap;
}

function getUomForPath(path, uomMap) {
    if (!path) return null;
    if (uomMap.has(path)) return uomMap.get(path) ?? null;

    const rootKey = String(path).split(/[.\[]/)[0];

    return uomMap.get(rootKey) ?? null;
}

function flattenMeasurementData(data, uomMap) {
    const rows = [];

    function walk(prefix, value) {
        if (!prefix) return;

        if (isPrimitive(value)) {
            rows.push({
                path: prefix,
                value,
                uom: getUomForPath(prefix, uomMap)
            });
            return;
        }

        if (Array.isArray(value)) {
            value.forEach((item, index) => {
                walk(prefix + '[' + index + ']', item);
            });
            return;
        }

        const keys = Object.keys(value || {});

        if (!keys.length) {
            rows.push({
                path: prefix,
                value: null,
                uom: getUomForPath(prefix, uomMap)
            });
            return;
        }

        keys.forEach((key) => {
            walk(prefix ? prefix + '.' + key : key, value[key]);
        });
    }

    Object.entries(data || {}).forEach(([key, value]) => {
        walk(key, value);
    });

    return rows;
}

function buildUnitMeasurementRows(optimizationPipelineDetail, cleanMeasurementData, mergedMeasurementData) {
    const measurementData = optimizationPipelineDetail?.merged_data || {};
    const uomMap = buildUomMap(cleanMeasurementData, mergedMeasurementData);
    const flatRows = flattenMeasurementData(measurementData, uomMap);

    return flatRows.map((row) => {
        const formattedValue = formatValue(row.value);
        const formattedUom = row.uom || '-';

        return {
            id: row.path,
            searchableText: row.path + ' ' + String(formattedValue) + ' ' + String(formattedUom),
            cells: {
                attribute: {
                    value: row.path,
                    tooltip: row.path
                },
                value: {
                    value: row.value,
                    displayValue: formattedValue,
                    tooltip: String(formattedValue)
                },
                uom: {
                    value: formattedUom
                }
            }
        };
    });
}

export function UnitMeasurementSidebarHeaderActions() {
    const { sidebar, openSidebar, optimizationPipelineDetail, getOnDemandResource, ts } = useOptimizationPipeline();
    const query = sidebar?.query || '';
    const cleanMeasurementData = getOnDemandResource('cleanMeasurementData')?.data;
    const mergedMeasurementData = getOnDemandResource('mergedMeasurementData')?.data;

    const rows = useMemo(() => {
        return buildUnitMeasurementRows(optimizationPipelineDetail, cleanMeasurementData, mergedMeasurementData);
    }, [optimizationPipelineDetail, cleanMeasurementData, mergedMeasurementData]);

    function handleExport(format) {
        exportSidebarTable({
            tableConfig: UNIT_MEASUREMENT_TABLE_CONFIG,
            rows,
            query,
            sort: sidebar?.sort,
            filename: buildExportFilename('unit-measurements', ts),
            format
        });
    }

    return (
        <div className='flex items-center gap-8'>
            <SidebarExportButtons onExcel={() => handleExport('excel')} onCsv={() => handleExport('csv')} disabled={!rows.length} />

            <SidebarHeaderSearch
                value={query}
                onChange={(nextQuery) => { openSidebar({ ...sidebar, query: nextQuery }); }}
            />
        </div>
    );
}

export default function UnitMeasurementSidebar() {
    const {
        sidebar,
        openSidebar,
        optimizationPipelineDetail,
        getOnDemandResource,
        ensureOnDemandResource
    } = useOptimizationPipeline();

    const query = sidebar?.query || '';
    const cleanMeasurementData = getOnDemandResource('cleanMeasurementData')?.data;
    const mergedMeasurementData = getOnDemandResource('mergedMeasurementData')?.data;

    useEffect(() => {
        ensureOnDemandResource('cleanMeasurementData');
        ensureOnDemandResource('mergedMeasurementData');
    }, [ensureOnDemandResource]);

    const rows = useMemo(() => {
        return buildUnitMeasurementRows(optimizationPipelineDetail, cleanMeasurementData, mergedMeasurementData);
    }, [optimizationPipelineDetail, cleanMeasurementData, mergedMeasurementData]);

    const loading = !optimizationPipelineDetail?.merged_data;

    return (
        <OptimizationPipelineSidebarTable
            tableConfig={UNIT_MEASUREMENT_TABLE_CONFIG}
            rows={rows}
            loading={loading}
            query={query}
            sort={sidebar?.sort}
            onQueryChange={(nextQuery) => {
                openSidebar({ ...sidebar, query: nextQuery });
            }}
            onSortChange={(nextSort) => {
                openSidebar({ ...sidebar, sort: nextSort });
            }}
        />
    );
}
